// Global search (GED-FE-08): the shapes the flyout and the results page share, where each kind of
// result leads, and the flat list of entries the keyboard moves through.
import type {
  SearchResultDtoOutputGroupsItem,
  SearchResultDtoOutputGroupsItemItemsItem,
} from '@batiflow/api-client';
import type { SearchKind } from '@batiflow/contracts';

export type SearchHit = SearchResultDtoOutputGroupsItemItemsItem;
export type SearchGroup = SearchResultDtoOutputGroupsItem;
export type SearchScope = 'all' | SearchKind;

/** Shorter queries are not sent (the API refuses them). */
export const MIN_QUERY_LENGTH = 2;
/** The API allows 60 searches a minute: wait for the typing to pause. */
export const DEBOUNCE_MS = 250;
/** Hits per kind in the flyout, and on the results page. */
export const FLYOUT_LIMIT = 5;
export const PAGE_LIMIT = 20;

/** Display order of the kinds. */
export const KIND_ORDER: readonly SearchKind[] = [
  'projects',
  'documents',
  'tasks',
  'clients',
  'messages',
];

export const isSearchKind = (value: string): value is SearchKind =>
  (KIND_ORDER as readonly string[]).includes(value);

const org = (orgId: string) => `/o/${encodeURIComponent(orgId)}`;

/**
 * Where a hit opens: a project, a document in the viewer of its project, a task in the tasks tab
 * of its project, a client, a message in its conversation. A document outside any project, and a task without its project (not
 * expected), lead to the nearest list.
 */
export function hitHref(orgId: string, hit: SearchHit): string {
  const id = encodeURIComponent(hit.id);
  const project = hit.projectId ? encodeURIComponent(hit.projectId) : null;
  switch (hit.kind) {
    case 'projects':
      return `${org(orgId)}/projects/${id}`;
    case 'documents':
      return project ? `${org(orgId)}/projects/${project}/documents/${id}` : `${org(orgId)}/files`;
    case 'tasks':
      return project
        ? `${org(orgId)}/projects/${project}/tasks?task=${id}`
        : `${org(orgId)}/projects`;
    case 'clients':
      return `${org(orgId)}/clients/${id}`;
    case 'messages':
      // A message hit opens its conversation at the message; without its channel (not expected)
      // the Chat app.
      return hit.channelId
        ? `${org(orgId)}/chat/${encodeURIComponent(hit.channelId)}?message=${id}`
        : `${org(orgId)}/chat`;
  }
}

/** The full results page for a query (and the kind of results, when it is not "all"). */
export function searchPageHref(orgId: string, q: string, scope: SearchScope = 'all'): string {
  const params = new URLSearchParams({ q });
  if (scope !== 'all') params.set('scope', scope);
  return `${org(orgId)}/search?${params.toString()}`;
}

/** One selectable line of the list: a hit, "see more of this kind" or "see all results". */
export interface SearchEntry {
  /** DOM id (aria-activedescendant). */
  id: string;
  type: 'hit' | 'more' | 'all';
  kind?: SearchKind;
  hit?: SearchHit;
  href: string;
}

export interface SearchSection {
  kind: SearchKind;
  entries: SearchEntry[];
}

export interface SearchListModel {
  sections: SearchSection[];
  /** "See all results", after the last group. */
  tail: SearchEntry | undefined;
  /** Every entry in reading order: what the arrow keys walk through. */
  entries: SearchEntry[];
  /** Number of hits shown. */
  count: number;
}

export interface BuildOptions {
  orgId: string;
  q: string;
  idPrefix: string;
  /** Hits shown per kind. */
  perGroup: number;
  /** Offer "see more" under a kind that has more (flyout) and "see all results" at the end. */
  seeMore: boolean;
}

/** Orders the groups, keeps the first hits of each and adds the "see more" lines. */
export function buildListModel(
  groups: readonly SearchGroup[],
  { orgId, q, idPrefix, perGroup, seeMore }: BuildOptions,
): SearchListModel {
  const sections: SearchSection[] = [];
  let count = 0;
  for (const kind of KIND_ORDER) {
    const group = groups.find((g) => g.kind === kind);
    if (!group || group.items.length === 0) continue;
    const shown = group.items.slice(0, perGroup);
    const entries: SearchEntry[] = shown.map((hit) => ({
      id: `${idPrefix}-${kind}-${hit.id}`,
      type: 'hit',
      kind,
      hit,
      href: hitHref(orgId, hit),
    }));
    count += shown.length;
    if (seeMore && (group.hasMore || group.items.length > perGroup))
      entries.push({
        id: `${idPrefix}-more-${kind}`,
        type: 'more',
        kind,
        href: searchPageHref(orgId, q, kind),
      });
    sections.push({ kind, entries });
  }
  const tail: SearchEntry | undefined =
    seeMore && sections.length > 0
      ? { id: `${idPrefix}-all`, type: 'all', href: searchPageHref(orgId, q) }
      : undefined;
  const entries = [...sections.flatMap((s) => s.entries), ...(tail ? [tail] : [])];
  return { sections, tail, entries, count };
}
